/**
 * @fileoverview Hook for aggregating per-platform ad spend data from ecommerce statistics.
 * @module shared/hooks/dashboard/useAdPlatformBreakdown
 */

import { useMemo } from 'react'
import { useDashboardData } from './useDashboardData'

/**
 * Ad platform breakdown data structure
 */
export interface AdPlatformBreakdownData {
  /** Platform name */
  platform: string
  /** Spend amount */
  amount: number
  /** Percentage of total ad spend */
  percentage: number
}

/**
 * Hook return type for ad platform breakdown
 */
interface UseAdPlatformBreakdownReturn {
  /** Array of platform breakdown data */
  data: AdPlatformBreakdownData[]
  /** Total ad spend across all platforms */
  totalSpend: number
  /** Loading state from ecommerce query */
  isLoading: boolean
  /** Error state from ecommerce query */
  isError: boolean
  /** Error message if query failed */
  error: string | null
}

/**
 * Hook to aggregate per-platform ad spend data from ecommerce statistics.
 *
 * Reads ecommerceQuery.dataPoints from DashboardDataProvider and aggregates
 * platformBreakdown across all data points, respecting current filters.
 *
 * @returns Aggregated platform breakdown with totals and percentages
 * @example
 * ```typescript
 * const { data, totalSpend, isLoading, error } = useAdPlatformBreakdown();
 * ```
 */
export function useAdPlatformBreakdown(): UseAdPlatformBreakdownReturn {
  const { salesTimeQuery } = useDashboardData()

  const { data, totalSpend } = useMemo((): {
    data: AdPlatformBreakdownData[]
    totalSpend: number
  } => {
    // Handle loading or error states
    if (!salesTimeQuery.data?.data || salesTimeQuery.data.data.length === 0) {
      return { data: [], totalSpend: 0 }
    }

    // Aggregate platform totals across all data points
    const platformTotals: Record<string, number> = {}

    for (const dataPoint of salesTimeQuery.data.data) {
      if (dataPoint.metrics.platformBreakdown) {
        for (const [platform, amount] of Object.entries(dataPoint.metrics.platformBreakdown)) {
          platformTotals[platform] = (platformTotals[platform] || 0) + amount
        }
      }
    }

    // Calculate total spend
    const totalSpend = Object.values(platformTotals).reduce((sum, amount) => sum + amount, 0)

    // Convert to array with percentages, handle edge case of zero total
    if (totalSpend === 0) {
      return { data: [], totalSpend: 0 }
    }

    const data: AdPlatformBreakdownData[] = Object.entries(platformTotals)
      .map(([platform, amount]) => ({
        platform,
        amount,
        percentage: Math.round((amount / totalSpend) * 100 * 100) / 100, // Round to 2 decimal places
      }))
      .sort((a, b) => b.amount - a.amount) // Sort by amount descending for stable legend

    return { data, totalSpend }
  }, [salesTimeQuery.data])

  return {
    data,
    totalSpend,
    isLoading: salesTimeQuery.isLoading,
    isError: salesTimeQuery.isError,
    error: salesTimeQuery.error?.message || null,
  }
}
